// District layout + walk-in zones. Pure data shared by the renderer (geometry, colliders)
// and the UI (zone prompts). The camera looks north (-z); the plaza is at the origin.

import type { ZoneAction } from "@degen/shared/config/zones";
export type { ZoneAction };

export interface Zone {
  id: string;
  name: string;
  x: number;
  z: number;
  r: number;
  actions: ZoneAction[];
  /** Smaller, more specific zones win over the big plaza zone. */
  priority: number;
}

export const ISLAND_RADIUS = 128;
export const WALK_RADIUS = 122;
/** The paved ring road (cars) and the spoke roads that connect it to the plaza. */
export const RING = { inner: 49, outer: 55 };
export const SPOKES: { x0: number; z0: number; x1: number; z1: number }[] = [
  { x0: 12, z0: 0, x1: 50, z1: 0 }, // east
  { x0: -12, z0: -6, x1: -50, z1: -6 }, // west
  { x0: 0, z0: 13, x1: 0, z1: 50 }, // south
  { x0: -11, z0: -8, x1: -11, z1: -49 }, // north
];
export const PLAYER_SPAWN = { x: 0, z: 8 };
/** People walking around outdoors (each one is an NPC account you can tap). */
export const CROWD_SIZE = 40;
/** Billboards that carry paid ads (world.billboard slot, §6.1: 4-6 boards); the rest show game slides. */
export const AD_BOARDS = [0, 2, 4, 6];

// Landmark centers. Every landmark sits NORTH of its zone (the camera looks north), so you
// see the building above you when you stand in its zone.
export const DISTRICTS = {
  trading: { x: 0, z: -38 },
  mint: { x: 31, z: -20 },
  launchpad: { x: -32, z: -24 },
  farm: { x: -32, z: 15 }, // field center
  home: { x: 35, z: 15 },
  cafe: { x: -13, z: 5 },
  park: { x: 12, z: 7 },
  petshop: { x: 20, z: 23 },
  // Auto Row (north, just past the ring road: showrooms open straight onto the road)
  polancco: { x: -26, z: -69 },
  abbja: { x: 0, z: -72 },
  mrjaay: { x: 26, z: -69 },
  dock: { x: 55, z: 110 },
  // Outer band (2026-10 expansion): every building here can be entered.
  missioncontrol: { x: -21, z: -27 }, // the Launchpad's control building
  vano: { x: 62, z: -66 },
  gadgets: { x: -62, z: -62 },
  zoomers: { x: -52, z: -92 },
  hackhub: { x: 96, z: -42 },
  arena: { x: -72, z: 66 },
  lighthouse: { x: -20, z: 108 },
};

export interface HouseSite {
  id: string;
  name: string;
  x: number;
  z: number;
  w: number;
  d: number;
}

/** Six houses for sale around the ring road. Fronts face south, zones sit just south. */
export const HOUSE_SITES: HouseSite[] = [
  { id: "h1", name: "Starter Flat", x: 64, z: -30, w: 9, d: 7 },
  { id: "h2", name: "Townhouse", x: 70, z: -6, w: 10, d: 8 },
  { id: "h3", name: "Garden Villa", x: 66, z: 18, w: 12, d: 9 },
  { id: "h4", name: "Studio Loft", x: -62, z: -34, w: 9, d: 7 },
  { id: "h5", name: "Creator House", x: -70, z: -10, w: 11, d: 8 },
  { id: "h6", name: "Whale Mansion", x: -66, z: 16, w: 14, d: 10 },
];

export const ZONES: Zone[] = [
  { id: "trading", name: "Trading Floor", x: 0, z: -23, r: 5, actions: ["enter", "pumpr", "wallet"], priority: 2 },
  { id: "mint", name: "Mint District", x: 31, z: -8, r: 5, actions: ["enter", "mintr"], priority: 2 },
  { id: "launchpad", name: "Launchpad", x: -31, z: -10, r: 5, actions: ["launch_coin", "launch_nft", "enter"], priority: 2 },
  { id: "missioncontrol", name: "Mission Control", x: -21, z: -19.5, r: 3, actions: ["enter", "launch_coin"], priority: 3 },
  { id: "vano", name: "Vano HQ", x: 62, z: -54, r: 4.5, actions: ["enter", "vano"], priority: 2 },
  { id: "gadgets", name: "Plug Gadgets", x: -62, z: -52, r: 4.5, actions: ["enter", "gadgets"], priority: 2 },
  { id: "zoomers", name: "Zoomers Moto", x: -52, z: -81, r: 4.5, actions: ["enter", "cars"], priority: 2 },
  { id: "hackhub", name: "Hackathon Hub", x: 96, z: -31, r: 4.5, actions: ["enter", "hackathon"], priority: 2 },
  { id: "arena", name: "Event Arena", x: -72, z: 80, r: 5, actions: ["enter", "events"], priority: 2 },
  { id: "lighthouse", name: "Lighthouse", x: -20, z: 115, r: 4, actions: ["enter", "grass"], priority: 2 },
  { id: "farm", name: "The Farm", x: -28, z: 28, r: 5.5, actions: ["enter", "farm"], priority: 2 },
  { id: "home", name: "Home", x: 35, z: 25.5, r: 5, actions: ["enter", "sleep", "laylow"], priority: 2 },
  { id: "petshop", name: "Pet Shop", x: 16, z: 31, r: 4.5, actions: ["enter", "pets"], priority: 2 },
  { id: "cafe", name: "Café", x: -9, z: 6, r: 3.5, actions: ["enter", "coffee", "chirp"], priority: 3 },
  { id: "park", name: "Park", x: 9, z: 8, r: 4, actions: ["grass", "chirp"], priority: 3 },
  { id: "polancco", name: "Polancco", x: -26, z: -59, r: 4.5, actions: ["enter", "cars"], priority: 2 },
  { id: "abbja", name: "Abbja Cars", x: 0, z: -62, r: 4.5, actions: ["enter", "cars"], priority: 2 },
  { id: "mrjaay", name: "Mr Jaay Autos", x: 26, z: -59, r: 4.5, actions: ["enter", "cars"], priority: 2 },
  { id: "dock", name: "Ferry Dock", x: 55, z: 110, r: 5, actions: ["messages"], priority: 2 },
  ...HOUSE_SITES.map((h) => ({ id: h.id, name: h.name, x: h.x, z: h.z + h.d / 2 + 4, r: 4, actions: ["enter", "houses"] as ZoneAction[], priority: 2 })),
  { id: "plaza", name: "Social Plaza", x: 0, z: 0, r: 12, actions: ["chirp"], priority: 1 },
];

/** Where you step off the ferry on day 1. */
export const ARRIVAL = { x: 55, z: 114 };
/** The ferry dock pokes out past WALK_RADIUS: this strip (planks + the beach to the island) is walkable. */
export const DOCK_WALK = { x0: 53.4, x1: 56.6, z0: 104, z1: 137 };
/** Inner districts get footpaths straight to the plaza; outer ones hang off the ring road. */
export const OUTER_ZONES = new Set(["polancco", "abbja", "mrjaay", "dock", "vano", "gadgets", "zoomers", "hackhub", "arena", "lighthouse", ...HOUSE_SITES.map((h) => h.id)]);

export function zoneAt(x: number, z: number): Zone | null {
  let best: Zone | null = null;
  for (const zn of ZONES) {
    if ((x - zn.x) ** 2 + (z - zn.z) ** 2 > zn.r * zn.r) continue;
    if (!best || zn.priority > best.priority) best = zn;
  }
  return best;
}

/** Map icons for the navigation map. */
export const ZONE_ICONS: Record<string, string> = {
  trading: "📈", mint: "🖼️", launchpad: "🚀", farm: "🌾", home: "🛏️", petshop: "🐾", cafe: "☕", park: "🌳", plaza: "🐦",
  polancco: "🚗", abbja: "🏎️", mrjaay: "🚀", dock: "⛴️",
  missioncontrol: "🛰️", vano: "🤖", gadgets: "📱", zoomers: "🏍️", hackhub: "🧑‍💻", arena: "🎤", lighthouse: "🗼",
  ...Object.fromEntries(HOUSE_SITES.map((h) => [h.id, "🏡"])),
};

/** Building footprints (center + size + map color), shared by the 3D world and the map. */
export interface Footprint {
  id: string;
  x: number;
  z: number;
  w: number;
  d: number;
  color: string;
}

export const FOOTPRINTS: Footprint[] = [
  { id: "trading", x: DISTRICTS.trading.x, z: DISTRICTS.trading.z, w: 12, d: 10, color: "#5b7299" },
  { id: "mint", x: DISTRICTS.mint.x, z: DISTRICTS.mint.z, w: 13, d: 9, color: "#7c3aed" },
  { id: "home", x: DISTRICTS.home.x, z: DISTRICTS.home.z, w: 10, d: 8, color: "#e7d8b9" },
  { id: "petshop", x: DISTRICTS.petshop.x, z: DISTRICTS.petshop.z, w: 9, d: 6, color: "#fde68a" },
  { id: "cafe", x: DISTRICTS.cafe.x - 1.5, z: DISTRICTS.cafe.z - 2.5, w: 6, d: 5, color: "#a16207" },
  { id: "farm", x: DISTRICTS.farm.x, z: DISTRICTS.farm.z, w: 15, d: 11, color: "#7a5230" },
  { id: "barn", x: DISTRICTS.farm.x - 12, z: DISTRICTS.farm.z - 1, w: 7, d: 6, color: "#b91c1c" },
  { id: "polancco", x: DISTRICTS.polancco.x, z: DISTRICTS.polancco.z, w: 16, d: 10, color: "#0ea5e9" },
  { id: "abbja", x: DISTRICTS.abbja.x, z: DISTRICTS.abbja.z, w: 16, d: 10, color: "#1f2937" },
  { id: "mrjaay", x: DISTRICTS.mrjaay.x, z: DISTRICTS.mrjaay.z, w: 16, d: 10, color: "#b45309" },
  { id: "missioncontrol", x: DISTRICTS.missioncontrol.x, z: DISTRICTS.missioncontrol.z, w: 8, d: 6, color: "#e2e8f0" },
  { id: "vano", x: DISTRICTS.vano.x, z: DISTRICTS.vano.z, w: 12, d: 10, color: "#1e1b4b" },
  { id: "gadgets", x: DISTRICTS.gadgets.x, z: DISTRICTS.gadgets.z, w: 12, d: 8, color: "#f8fafc" },
  { id: "zoomers", x: DISTRICTS.zoomers.x, z: DISTRICTS.zoomers.z, w: 16, d: 10, color: "#f97316" },
  { id: "hackhub", x: DISTRICTS.hackhub.x, z: DISTRICTS.hackhub.z, w: 14, d: 10, color: "#334155" },
  { id: "arena", x: DISTRICTS.arena.x, z: DISTRICTS.arena.z, w: 22, d: 16, color: "#0f766e" },
  { id: "lighthouse", x: DISTRICTS.lighthouse.x, z: DISTRICTS.lighthouse.z, w: 6, d: 6, color: "#f4f4f5" },
  ...HOUSE_SITES.map((h) => ({ id: h.id, x: h.x, z: h.z, w: h.w, d: h.d, color: "#fca5a5" })),
];

/** Front doors: walk into one (heading north) to go inside. Points sit just outside the wall. */
export const DOORS: { id: string; x: number; z: number }[] = [
  { id: "home", x: DISTRICTS.home.x, z: DISTRICTS.home.z + 4 + 0.7 },
  { id: "cafe", x: DISTRICTS.cafe.x - 1.5, z: DISTRICTS.cafe.z + 0.7 },
  { id: "trading", x: DISTRICTS.trading.x, z: DISTRICTS.trading.z + 5 + 0.7 },
  { id: "mint", x: DISTRICTS.mint.x, z: DISTRICTS.mint.z + 4.5 + 0.7 },
  { id: "farm", x: DISTRICTS.farm.x - 12, z: DISTRICTS.farm.z + 2 + 0.7 },
  { id: "petshop", x: DISTRICTS.petshop.x, z: DISTRICTS.petshop.z + 3 + 0.7 },
  { id: "polancco", x: DISTRICTS.polancco.x, z: DISTRICTS.polancco.z + 5 + 0.7 },
  { id: "abbja", x: DISTRICTS.abbja.x, z: DISTRICTS.abbja.z + 5 + 0.7 },
  { id: "mrjaay", x: DISTRICTS.mrjaay.x, z: DISTRICTS.mrjaay.z + 5 + 0.7 },
  { id: "launchpad", x: DISTRICTS.missioncontrol.x, z: DISTRICTS.missioncontrol.z + 3 + 0.7 },
  { id: "vano", x: DISTRICTS.vano.x, z: DISTRICTS.vano.z + 5 + 0.7 },
  { id: "gadgets", x: DISTRICTS.gadgets.x, z: DISTRICTS.gadgets.z + 4 + 0.7 },
  { id: "zoomers", x: DISTRICTS.zoomers.x, z: DISTRICTS.zoomers.z + 5 + 0.7 },
  { id: "hackhub", x: DISTRICTS.hackhub.x, z: DISTRICTS.hackhub.z + 5 + 0.7 },
  { id: "arena", x: DISTRICTS.arena.x, z: DISTRICTS.arena.z + 8 + 0.7 },
  { id: "lighthouse", x: DISTRICTS.lighthouse.x, z: DISTRICTS.lighthouse.z + 3 + 0.7 },
  ...HOUSE_SITES.map((h) => ({ id: h.id, x: h.x, z: h.z + h.d / 2 + 0.7 })),
];
